import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';

import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';

import useStyles from './sub-risk.styles';

const radioOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const SwimmingPoolForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();

  const poolType = getIn(values, `${fieldName}.building.swimmingPool.typeOfPool`);

  const getRadioValue = (value) => {
    if (value === null) {
      return undefined;
    }

    return value ? 'true' : 'false';
  };

  // Auto-expand when pool type is null or undefined (not yet answered)
  const shouldAutoExpand = poolType === null || poolType === undefined;

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Swimming Pools</div>}
      initialOpen={shouldAutoExpand}
    >
      <Grid container spacing={2}>
        <Field
          xs={12}
          type="select"
          id={`${fieldName}.building.swimmingPool.typeOfPool`}
          name={`${fieldName}.building.swimmingPool.typeOfPool`}
          mode="dark"
          label="Type Of Pool"
          options={lookupsJson.farm.swimmingPoolTypes}
          disabled={disabled}
          onChange={(val) => {
            if (val === 'N') {
              setFieldValue(`${fieldName}.coverages.poolLimit`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`, undefined);
              setFieldValue(`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`, undefined);
            }
          }}
        />

        {poolType === 'A' && (
          <Field
            xs={12}
            type="numeric"
            id={`${fieldName}.coverages.poolLimit`}
            name={`${fieldName}.coverages.poolLimit`}
            mode="dark"
            label="Additional increased limit"
            description={`Included: $2,500, Total: $${(parseInt(getIn(values, `${fieldName}.coverages.poolLimit`) || 0) + 2500).toLocaleString()}`}
            descriptionClassName={classes.fieldHelperText}
            format={{ thousandSeparator: true, prefix: '$' }}
            disabled={disabled}
          />
        )}

        {poolType === 'I' && (
          <Field
            xs={12}
            type="numeric"
            id={`${fieldName}.coverages.poolLimit`}
            name={`${fieldName}.coverages.poolLimit`}
            mode="dark"
            label="Coverage limit"
            format={{ thousandSeparator: true, prefix: '$' }}
            disabled={disabled}
          />
        )}

        {poolType && poolType !== 'N' && (
          <>
            {poolType === 'I' ? (
              <>
                <Grid item xs={12}>
                  <Label type="infoValueLight" className={classes.radioLabel}>
                    Is there a minimum 4ft high fence with self-latching gate?
                  </Label>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`}
                  name={`${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`}
                  mode="dark"
                  value={getRadioValue(
                    getIn(values, `${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`)
                  )}
                  onChange={(e) => {
                    setFieldValue(
                      `${fieldName}.building.swimmingPool.has4FtHighFenceWithSelfLatchingGate`,
                      e.target.value === 'true'
                    );
                  }}
                  options={radioOptions}
                  disabled={disabled}
                />
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Label type="infoValueLight" className={classes.radioLabel}>
                    Are there removable steps or a locking gate with decking around pool?
                  </Label>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`}
                  name={`${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`}
                  mode="dark"
                  value={getRadioValue(
                    getIn(values, `${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`)
                  )}
                  onChange={(e) => {
                    setFieldValue(
                      `${fieldName}.building.swimmingPool.hasRemovableStepsOrLockingGate`,
                      e.target.value === 'true'
                    );
                  }}
                  options={radioOptions}
                  disabled={disabled}
                />
              </>
            )}

            <Grid item xs={12}>
              <Label type="infoValueLight" className={classes.radioLabel}>
                Is there a diving board or pool slide?
              </Label>
            </Grid>
            <Field
              xs={12}
              type="radio"
              id={`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`}
              name={`${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`}
              mode="dark"
              value={getRadioValue(getIn(values, `${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`))}
              onChange={(e) => {
                setFieldValue(
                  `${fieldName}.building.swimmingPool.hasDivingBoardOrPoolSlide`,
                  e.target.value === 'true'
                );
              }}
              options={radioOptions}
              disabled={disabled}
            />
          </>
        )}
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

SwimmingPoolForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

SwimmingPoolForm.defaultProps = {
  disabled: false
};

export default observer(SwimmingPoolForm);
